import { ComponentType, LazyExoticComponent, ReactNode, Suspense, useState, Component, ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DashboardSkeleton } from '@/components/ui/skeleton';
import { useTranslation } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';

interface LazyRouteWrapperProps {
  lazyComponent: LazyExoticComponent<ComponentType<any>>;
  fallback?: ReactNode;
  routeName?: string;
}

interface ErrorState {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
}

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second

// Helper to check if error is a chunk load error
const isChunkLoadError = (error: Error | null): boolean => {
  if (!error) return false;
  const message = error.message.toLowerCase();
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('loading chunk') ||
    message.includes('chunk load error') ||
    message.includes('loading css chunk') ||
    message.includes('importing a module script failed')
  );
};

// Report error to backend
const reportError = async (error: Error, routeName?: string) => {
  try {
    await supabase.functions.invoke('report-error', {
      body: {
        error_message: error.message,
        error_stack: error.stack,
        url: window.location.href,
        user_agent: navigator.userAgent,
        metadata: {
          timestamp: new Date().toISOString(),
          pathname: window.location.pathname,
          routeName,
          errorType: 'chunk_load_error',
        },
      },
    });
  } catch (reportError) {
    console.error('Failed to report error:', reportError);
  }
};

// Error boundary for chunk load errors
class ChunkLoadErrorBoundary extends Component<
  { 
    children: ReactNode; 
    onError: (error: Error) => void;
    routeName?: string;
  },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode; onError: (error: Error) => void; routeName?: string }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    if (isChunkLoadError(error)) {
      return { hasError: true, error };
    }
    // Re-throw non-chunk errors
    throw error;
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isChunkLoadError(error)) {
      this.props.onError(error);
      reportError(error, this.props.routeName);
    }
  }

  render() {
    if (this.state.hasError && this.state.error) {
      // Let parent handle the error UI
      return null;
    }
    return this.props.children;
  }
}

export function LazyRouteWrapper({ 
  lazyComponent, 
  fallback = <DashboardSkeleton />,
  routeName 
}: LazyRouteWrapperProps) {
  const { t } = useTranslation();
  const [errorState, setErrorState] = useState<ErrorState>({
    hasError: false,
    error: null,
    retryCount: 0,
  });
  const [retryKey, setRetryKey] = useState(0);

  const handleError = (error: Error) => {
    setErrorState(prev => ({
      hasError: true,
      error,
      retryCount: prev.retryCount,
    }));
  };

  const handleRetry = async () => {
    if (errorState.retryCount >= MAX_RETRIES) {
      // Reset after max retries to allow another attempt cycle
      setErrorState({
        hasError: false,
        error: null,
        retryCount: 0,
      });
      setRetryKey(prev => prev + 1);
      return;
    }

    const delay = INITIAL_RETRY_DELAY * Math.pow(2, errorState.retryCount);
    
    // Wait with exponential backoff
    await new Promise(resolve => setTimeout(resolve, delay));

    // Force reload the chunk by updating the retry key and clearing cache
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }

    setRetryKey(prev => prev + 1);
    setErrorState(prev => ({
      hasError: false,
      error: null,
      retryCount: prev.retryCount + 1,
    }));
  };

  // Show error UI if chunk load failed
  if (errorState.hasError && errorState.error) {
    const canRetry = errorState.retryCount < MAX_RETRIES;

    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-destructive/20">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle className="text-xl">
              {t('errorBoundary.chunkLoadError.title', 'Failed to Load Page')}
            </CardTitle>
            <CardDescription>
              {t('errorBoundary.chunkLoadError.description', 'The page could not be loaded. This may be due to a network issue or outdated cache.')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {canRetry ? (
              <>
                <p className="text-sm text-muted-foreground text-center">
                  {t('errorBoundary.chunkLoadError.retryMessage', `Attempt ${errorState.retryCount + 1} of ${MAX_RETRIES}`)}
                </p>
                <Button 
                  onClick={handleRetry} 
                  className="w-full"
                  variant="default"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {t('errorBoundary.chunkLoadError.retry', 'Try Again')}
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground text-center">
                  {t('errorBoundary.chunkLoadError.maxRetries', 'Maximum retry attempts reached.')}
                </p>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => window.location.reload()} 
                    className="flex-1"
                    variant="default"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    {t('errorBoundary.tryAgain', 'Reload Page')}
                  </Button>
                  <Button 
                    onClick={() => window.location.href = '/dashboard'} 
                    className="flex-1"
                    variant="outline"
                  >
                    {t('errorBoundary.goHome', 'Go Home')}
                  </Button>
                </div>
              </>
            )}
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-4">
                <summary className="text-xs text-muted-foreground cursor-pointer">
                  Error Details
                </summary>
                <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto max-h-32">
                  {errorState.error.message}
                </pre>
              </details>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render the lazy component with Suspense and error boundary
  const Component = lazyComponent;
  
  return (
    <ChunkLoadErrorBoundary onError={handleError} routeName={routeName} key={retryKey}>
      <Suspense fallback={fallback} key={retryKey}>
        <Component key={retryKey} />
      </Suspense>
    </ChunkLoadErrorBoundary>
  );
}
